import { Component, ElementRef, Input } from '@angular/core';
import * as d3 from 'd3';

@Component({
  selector: 'app-line-chart-consumption',
  template: `
    <figure id="line-chart-consumption"></figure>
  `,
  styleUrls: []
})
export class LineChartConsumptionComponent {
  @Input() data: any;
  @Input() public start_date: any;
  @Input() public end_date: any;

  private svg: any;
  width = 300;
  height = 300; // Increased height
  margin = {
    top: 20,
    right: 20,
    bottom: 40, // Increased bottom margin for rotated labels
    left: 40    // Increased left margin for y-axis labels
  };
  radius: any;
  duration = 250;

  public yScale: any;
  public xScale: any;
  public xAxis: any;
  public yAxis: any;
  public lineGroup: any;

  lineOpacity = "1";
  lineOpacityHover = "0.85";
  otherLinesOpacityHover = "0.1";
  lineStroke = "1.5px";
  lineStrokeHover = "2.5px";

  circleOpacity = '1';
  circleOpacityOnLineHover = "0.25"
  circleRadius = 12;
  circleRadiusHover = 12;

  constructor(
    public chartElem: ElementRef,
  ) { }

  ngOnInit(): void {
    this.createSvg();
    this.drawChart(this.data);
  }

  private createSvg(): void {
    const myElement = document.getElementById('cardConsumptions');
    if (myElement) {
      this.width = myElement.getBoundingClientRect().width - (this.margin.left + this.margin.right);
    }

    this.svg = d3.select("figure#line-chart-consumption")
        .append("svg")
        .attr("width", this.width + this.margin.left + this.margin.right)
        .attr("height", this.height + this.margin.top + this.margin.bottom)
        .append("g")
        .attr("transform", `translate(${this.margin.left},${this.margin.top})`);
  }

  simpleGroup(array: any[], key: string) {
    const groupedData = array.reduce((acc, obj) => {
      const keyValue = obj[key];
      if (!acc[keyValue]) {
        acc[keyValue] = [];
      }
      acc[keyValue].push(obj);
      return acc;
    }, {});
  
    return { keys: Object.keys(groupedData), data: groupedData };
  }

  groupBy(array: any[], key: string) {
    const groupedData = array.reduce((acc, obj) => {
      const keyValue = obj[key];
      if (!acc[keyValue]) {
        acc[keyValue] = [];
      }
      acc[keyValue].push(obj);
      return acc;
    }, {});
  
    return { keys: Object.keys(groupedData), data: groupedData };
  }

  private drawChart(data: any[]): void {    
    if (data == undefined || data.length === 0) {
      return;
    }

    this.svg.selectAll("*").remove();

    // Group data by month/year and sum the liters, adjusting for week spanning across years
    const monthlyData = data.reduce((acc, d) => {
      // For week 1, if it starts in December, it belongs to the next year
      let year = d.year;
      let month = d.month;
      
      // Adjust year-week boundary
      if (d.week === 1 && d.month === 12) {
        year = year + 1;
        month = 1;
      }
      // Adjust for week 52/53 in January
      if (d.week >= 52 && d.month === 1) {
        year = year - 1;
        month = 12;
      }

      const key = `${year}-${String(month).padStart(2, '0')}`;
      const displayKey = `${String(month).padStart(2, '0')}/${String(year).slice(2)}`;
      
      if (!acc[key]) {
        acc[key] = {
          year: year,
          month: month,
          liters: 0,
          sortKey: key,
          displayKey: displayKey
        };
      }
      acc[key].liters += d.liters;
      return acc;
    }, {});

    // Convert to array and sort
    const processedData = Object.values(monthlyData);
    processedData.sort((a: any, b: any) => a.sortKey.localeCompare(b.sortKey));

    // Create display labels for x-axis
    const xAxisLabels = processedData.map((d: any) => d.displayKey);

    // Update scales
    this.xScale = d3.scaleBand()      
      .domain(xAxisLabels)
      .range([0, this.width])
      .padding(0.1);

    this.yScale = d3.scaleLinear()
      .domain([0, d3.max(processedData, (d: any) => d.liters) || 0])
      .range([this.height - this.margin.bottom, 0]);

    // Add background fills for alternating months
    let isEvenMonth = false;
    processedData.forEach((d: any, i) => {
      isEvenMonth = !isEvenMonth;
      const startX = this.xScale(d.displayKey);
      const endX = startX + this.xScale.bandwidth();

      if (isEvenMonth) {
        this.svg.append("rect")
          .attr("x", startX)
          .attr("y", 0)
          .attr("width", this.xScale.bandwidth())
          .attr("height", this.height - this.margin.bottom)
          .attr("fill", "#f5f5f5")
          .attr("opacity", 0.5);
      }
    });

    // Add vertical lines for year changes
    let currentYear = -1;
    processedData.forEach((d: any, i) => {
      if (d.year !== currentYear) {
        if (currentYear !== -1) { // Don't draw line for the first year
          const xPosition = this.xScale(d.displayKey);
          
          // Add vertical dashed line
          this.svg.append("line")
            .attr("x1", xPosition)
            .attr("x2", xPosition)
            .attr("y1", 0)
            .attr("y2", this.height - this.margin.bottom)
            .attr("stroke", "#666")
            .attr("stroke-width", 1)
            .attr("stroke-dasharray", "5,5");

          // Add year label
          this.svg.append("text")
            .attr("x", xPosition + 5)
            .attr("y", 15)
            .attr("fill", "#666")
            .attr("font-size", "12px")
            .text(d.year);
        }
        currentYear = d.year;
      }
    });

    // Draw axes
    // X axis
    this.svg.append("g")
      .attr("transform", `translate(0,${this.height - this.margin.bottom})`)
      .call(d3.axisBottom(this.xScale))
      .selectAll("text")
      .attr("transform", "translate(-10,10)rotate(-45)")
      .style("text-anchor", "end")
      .style("font-size", "12px");

    // Y axis
    this.svg.append("g")
      .call(d3.axisLeft(this.yScale)
        .tickFormat((d: any) => `${d/1000}K`)
        .ticks(5));

    // Draw line
    const lineGenerator = d3.line<any>()
      .x(d => (this.xScale(d.displayKey) || 0) + this.xScale.bandwidth() / 2)
      .y(d => this.yScale(d.liters))
      .curve(d3.curveMonotoneX);

    const lines = this.svg
      .append('g')
      .attr('class', 'lines');

    lines
      .datum(processedData)
      .append('path')
      .attr("d", lineGenerator)
      .style('fill', 'none')
      .attr("stroke", "orange")
      .attr("stroke-width", 4)
      .style('opacity', this.lineOpacity);

    // Add dots
    this.svg
      .selectAll("dots")
      .data(processedData)
      .enter()
      .append("circle")
      .attr("cx", (d: any) => (this.xScale(d.displayKey) || 0) + this.xScale.bandwidth() / 2)
      .attr("cy", (d: any) => this.yScale(d.liters))
      .attr("r", 4)
      .style("fill", "orange");

    // Add Y axis label
    this.svg.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - this.margin.left)
      .attr("x", 0 - (this.height / 2))
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text("Litros (K)");
  }

}
